/**
 * 🚗 HOST VEHICLE DETAIL VIEWER
 * Component for detailed review of host vehicle applications
 * Shows all documents, photos, and vehicle information
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RotateRight as RotateRightIcon,
  Person,
  DirectionsCar,
  Description,
  Security,
  CheckCircle,
  Cancel,
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  AttachMoney,
  Verified
} from '@mui/icons-material';
import { hostVehicleService, type HostVehicleApplication } from '@/services/hostVehicleService';

interface HostVehicleDetailViewerProps {
  open: boolean;
  onClose: () => void;
  application: HostVehicleApplication;
  onApprove?: (vehicleId: string, notes?: string) => Promise<void>;
  onReject?: (vehicleId: string, reason: string, notes?: string) => Promise<void>;
}

interface ImageViewState {
  zoom: number;
  rotation: number;
  currentImageIndex: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vehicle-tabpanel-${index}`}
      aria-labelledby={`vehicle-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const HostVehicleDetailViewer: React.FC<HostVehicleDetailViewerProps> = ({
  open,
  onClose,
  application,
  onApprove,
  onReject
}) => {
  const [imageViewState, setImageViewState] = useState<ImageViewState>({
    zoom: 1,
    rotation: 0,
    currentImageIndex: 0
  });
  const [loadingImages, setLoadingImages] = useState<string[]>([]);
  const [imageErrors, setImageErrors] = useState<string[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleImageLoad = (imagePath: string) => {
    setLoadingImages(prev => prev.filter(path => path !== imagePath));
  };

  const handleImageError = (imagePath: string) => {
    console.error('🚨 Image failed to load:', imagePath);
    setLoadingImages(prev => prev.filter(path => path !== imagePath));
    setImageErrors(prev => [...prev, imagePath]);
  };

  const handleZoomIn = () => {
    setImageViewState(prev => ({
      ...prev,
      zoom: Math.min(prev.zoom + 0.25, 3)
    }));
  };

  const handleZoomOut = () => {
    setImageViewState(prev => ({
      ...prev,
      zoom: Math.max(prev.zoom - 0.25, 0.5)
    }));
  };

  const handleRotate = () => {
    setImageViewState(prev => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360
    }));
  };

  const handleDownloadDocument = async (imagePath: string, filename: string) => {
    setIsDownloading(true);
    try {
      await hostVehicleService.downloadDocument(imagePath, filename);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending_review: 'warning',
      approved: 'success',
      rejected: 'error',
      incomplete: 'default'
    } as const;
    return colors[status as keyof typeof colors] || 'default';
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      economy: 'default',
      premium: 'primary',
      luxury: 'secondary',
      suv: 'success',
      electric: 'info'
    } as const;
    return colors[category as keyof typeof colors] || 'default';
  };

  const documentImages = [
    ...(application.vehicle_registration_url ? [{ label: 'Vehicle Registration', url: application.vehicle_registration_url }] : []),
    ...(application.insurance_certificate_url ? [{ label: 'Insurance Certificate', url: application.insurance_certificate_url }] : []),
    ...(application.driver_license_url ? [{ label: 'Driver License', url: application.driver_license_url }] : []),
    ...(application.vehicle_photos?.map((url, index) => ({
      label: `Vehicle Photo ${index + 1}`,
      url
    })) || [])
  ];

  const currentImage = documentImages[imageViewState.currentImageIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" component="div">
              Host Vehicle Application Details
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip 
                label={application.vehicle_type.toUpperCase()}
                color={getCategoryColor(application.vehicle_type)}
                size="small"
              />
              <Chip 
                label={application.status.replace('_', ' ').toUpperCase()}
                color={getStatusColor(application.status)}
                size="small"
              />
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Host Information" icon={<Person />} />
            <Tab label="Vehicle Details" icon={<DirectionsCar />} />
            <Tab label="Documents" icon={<Description />} />
            <Tab label="Insurance" icon={<Security />} />
          </Tabs>
        </Box>

        {/* Host Information Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ mr: 2, width: 56, height: 56 }}>
                      {application.host_name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">
                        {application.host_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Host Applicant
                      </Typography>
                    </Box>
                  </Box>
                  
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <Email />
                      </ListItemIcon>
                      <ListItemText primary={application.host_email} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Phone />
                      </ListItemIcon>
                      <ListItemText primary={application.host_phone} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <LocationOn />
                      </ListItemIcon>
                      <ListItemText primary={application.host_address} />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Application Timeline
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <CalendarToday />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Submitted"
                        secondary={new Date(application.submitted_at).toLocaleString()}
                      />
                    </ListItem>
                    
                    {application.reviewed_at && (
                      <ListItem>
                        <ListItemIcon>
                          {application.status === 'approved' ? <CheckCircle /> : <Cancel />}
                        </ListItemIcon>
                        <ListItemText 
                          primary={application.status === 'approved' ? 'Approved' : 'Rejected'}
                          secondary={new Date(application.reviewed_at).toLocaleString()}
                        />
                      </ListItem>
                    )}
                  </List>
                  
                  {application.admin_notes && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Admin Notes:
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body2">
                          {application.admin_notes}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                  
                  {application.rejection_reason && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        Rejection Reason:
                      </Typography>
                      <Paper sx={{ p: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
                        <Typography variant="body2">
                          {application.rejection_reason}
                        </Typography>
                      </Paper>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Vehicle Details Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Vehicle Information
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemText 
                        primary="Make & Model"
                        secondary={`${application.make} ${application.model}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Year"
                        secondary={application.year}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Color"
                        secondary={application.color}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText 
                        primary="Plate Number"
                        secondary={application.plate_number}
                      />
                    </ListItem>
                    {application.vin && (
                      <ListItem>
                        <ListItemText 
                          primary="VIN"
                          secondary={application.vin}
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemText 
                        primary="Vehicle Type"
                        secondary={
                          <Chip 
                            label={application.vehicle_type.toUpperCase()}
                            color={getCategoryColor(application.vehicle_type)}
                            size="small"
                          />
                        }
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            {/* Document Thumbnails */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                Documents & Photos ({documentImages.length})
              </Typography>
              
              <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                <Grid container spacing={1}>
                  {documentImages.map((doc, index) => (
                    <Grid item xs={6} key={index}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer',
                          border: imageViewState.currentImageIndex === index ? 2 : 0,
                          borderColor: 'primary.main',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: 2
                          }
                        }}
                        onClick={() => setImageViewState(prev => ({ 
                          ...prev, 
                          currentImageIndex: index,
                          zoom: 1,
                          rotation: 0
                        }))}
                      >
                        <CardMedia
                          component="img"
                          height="80"
                          image={hostVehicleService.getDocumentImageUrl(doc.url)}
                          alt={doc.label}
                          sx={{ 
                            objectFit: 'cover',
                            backgroundColor: 'grey.100'
                          }}
                        />
                        <CardContent sx={{ p: 1 }}>
                          <Typography variant="caption" display="block" textAlign="center">
                            {doc.label}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                {documentImages.length === 0 && (
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    alignItems="center" 
                    justifyContent="center"
                    sx={{ height: 200, bgcolor: 'grey.50', borderRadius: 1 }}
                  >
                    <Description fontSize="large" color="disabled" />
                    <Typography variant="body2" color="textSecondary" mt={1}>
                      No documents uploaded
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Main Image Viewer */}
            <Grid item xs={12} md={8}>
              <Card sx={{ height: '400px', position: 'relative' }}>
                {/* Image Controls */}
                <Box 
                  position="absolute" 
                  top={8} 
                  right={8} 
                  zIndex={1}
                  display="flex"
                  gap={1}
                >
                  <Tooltip title="Zoom In">
                    <IconButton 
                      onClick={handleZoomIn}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                    >
                      <ZoomInIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Zoom Out">
                    <IconButton 
                      onClick={handleZoomOut}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                    >
                      <ZoomOutIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Rotate">
                    <IconButton 
                      onClick={handleRotate}
                      size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                    >
                      <RotateRightIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Download">
                    <IconButton 
                      onClick={() => currentImage && handleDownloadDocument(
                        currentImage.url, 
                        `${application.vehicle_id}_${currentImage.label.replace(' ', '_')}.jpg`
                      )}
                      size="small"
                      disabled={isDownloading}
                      sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                    >
                      {isDownloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Main Image Display */}
                <Box 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center" 
                  height="100%"
                  overflow="hidden"
                  p={2}
                >
                  {currentImage && (
                    <img
                      src={hostVehicleService.getDocumentImageUrl(currentImage.url)}
                      alt={currentImage.label}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        transform: `scale(${imageViewState.zoom}) rotate(${imageViewState.rotation}deg)`,
                        transition: 'transform 0.3s ease',
                        objectFit: 'contain'
                      }}
                      onLoad={() => handleImageLoad(currentImage.url)}
                      onError={() => handleImageError(currentImage.url)}
                    />
                  )}
                </Box>
                
                <Box position="absolute" bottom={8} left={8}>
                  <Typography variant="caption" sx={{ 
                    bgcolor: 'rgba(0,0,0,0.7)', 
                    color: 'white', 
                    px: 1, 
                    borderRadius: 1 
                  }}>
                    {currentImage?.label} ({imageViewState.currentImageIndex + 1}/{documentImages.length})
                  </Typography>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Insurance Tab */}
        <TabPanel value={tabValue} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Insurance Details
                  </Typography>
                  
                  <List dense>
                    {application.insurance_provider && (
                      <ListItem>
                        <ListItemText 
                          primary="Provider"
                          secondary={application.insurance_provider}
                        />
                      </ListItem>
                    )}
                    {application.insurance_policy_number && (
                      <ListItem>
                        <ListItemText 
                          primary="Policy Number"
                          secondary={application.insurance_policy_number}
                        />
                      </ListItem>
                    )}
                    {application.insurance_coverage && (
                      <ListItem>
                        <ListItemText 
                          primary="Coverage"
                          secondary={application.insurance_coverage}
                        />
                      </ListItem>
                    )}
                    {application.insurance_expiry_date && (
                      <ListItem>
                        <ListItemText 
                          primary="Expiry Date"
                          secondary={new Date(application.insurance_expiry_date).toLocaleDateString()}
                        />
                      </ListItem>
                    )}
                    {application.monthly_premium && (
                      <ListItem>
                        <ListItemIcon>
                          <AttachMoney />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Monthly Premium"
                          secondary={`₦${application.monthly_premium.toLocaleString()}`}
                        />
                      </ListItem>
                    )}
                    {!application.insurance_provider && !application.insurance_policy_number && (
                      <ListItem>
                        <ListItemText 
                          primary="Insurance Information"
                          secondary="Not provided or pending"
                        />
                      </ListItem>
                    )}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      {/* Action Buttons */}
      <DialogActions>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
          
          {application.status === 'pending_review' && (
            <Box display="flex" gap={1}>
              <Button 
                onClick={async () => {
                  setIsProcessing(true);
                  try {
                    await onReject?.(application.vehicle_id, 'Application rejected', 'Rejected from detail view');
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                color="error"
                variant="contained"
                disabled={isProcessing}
              >
                Reject
              </Button>
              <Button 
                onClick={async () => {
                  setIsProcessing(true);
                  try {
                    await onApprove?.(application.vehicle_id, 'Approved from detail view');
                  } finally {
                    setIsProcessing(false);
                  }
                }}
                color="success"
                variant="contained"
                disabled={isProcessing}
                startIcon={<Verified />}
              >
                {isProcessing ? 'Approving...' : 'Approve Vehicle'}
              </Button>
            </Box>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default HostVehicleDetailViewer;